import { useEffect, useRef, useState } from "react";
import Picture from "../Picture/Picture.jsx";
import CodeChip from "../CodeChip/CodeChip.jsx";
import styles from "./AnalysesList.module.css";

const WIDE = "(min-width: 64rem)";
const shot = (key) => `/images/123bimlive/${key}`;

/**
 * Every analysis as one large line of type, the way a report's contents page lists them.
 * On a wide screen the row crossing the middle of the viewport lights up and a sticky frame
 * beside the list shows its image and what it finds; hovering a row does the same. On a
 * phone the rows become a swipeable row of cards, each with its own image; `id` lands on
 * that row, for ScrollArrows to drive.
 */
export default function AnalysesList({ id, items }) {
  const [active, setActive] = useState(0);
  const rows = useRef([]);

  useEffect(() => {
    if (!window.matchMedia(WIDE).matches) return undefined;
    // A thin band across the middle of the viewport: whichever row is in it is the one on show.
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActive(Number(entry.target.dataset.index));
        }
      },
      { rootMargin: "-48% 0px -48% 0px" }
    );
    for (const row of rows.current) if (row) io.observe(row);
    return () => io.disconnect();
  }, []);

  const current = items[active];
  return (
    <div className={styles.root}>
      <div className={styles.media} aria-hidden="true">
        <div className={styles.frame}>
          {items.map((a, i) => (
            <Picture
              key={a.key}
              src={shot(a.key)}
              alt=""
              widths={[480, 960]}
              fallbackWidth={960}
              sizes="(min-width: 64rem) 40vw, 1px"
              className={styles.shot}
              imgClassName={i === active ? styles.shotActive : ""}
            />
          ))}
        </div>
        <p className={styles.caption}>
          <span className={styles.code} style={{ "--tint": current.tint }}>
            {current.code}
          </span>
          {current.gets}
        </p>
      </div>

      <ul id={id} className={styles.list}>
        {items.map((a, i) => (
          <li
            key={a.key}
            ref={(el) => {
              rows.current[i] = el;
            }}
            data-index={i}
            data-active={i === active ? "" : undefined}
            className={styles.row}
            onMouseEnter={() => setActive(i)}
          >
            <Picture
              src={shot(a.key)}
              alt={a.alt}
              widths={[480, 960]}
              fallbackWidth={960}
              sizes="80vw"
              className={styles.cardImage}
            />
            <span className={styles.rowCode} aria-hidden="true">
              <CodeChip code={a.code} tint={a.tint} />
            </span>
            <span className={styles.rowTitle}>{a.title}</span>
            <span className={styles.rowGets}>{a.gets}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
